import { useMemo } from 'react';
import { SignificanceCard } from '../components/significance-card';
import { images } from '../constants/images';
import { Trans, useTranslation } from 'react-i18next';

export type Significance = 'spiritual' | 'cultural' | 'philosophical';

const significanceIcons = {
  spiritual: (
    // Calendar/Moon icon for sacred month
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  ),
  cultural: (
    // Tent/home icon for Kalpwas tradition
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
      />
    </svg>
  ),
  philosophical: (
    // Star/sparkle icon for celestial alignments
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
      />
    </svg>
  ),
};

export const KumbhSignificanceSection = () => {
  const { t } = useTranslation(['home', 'common']);

  const significanceOfMagh = useMemo(
    () => ({
      spiritual: {
        title: t('home:significance.spiritual.title'),
        icon: significanceIcons.spiritual,
        content: (
          <Trans i18nKey="home:significance.spiritual.content">
            The month of Magh (January-February) is considered one of the most auspicious
            periods in the Hindu calendar. Ancient scriptures like the Padma Purana
            declare that bathing at the Triveni Sangam during Magh grants immense
            spiritual merit, washing away sins accumulated over many lifetimes. Each dawn
            dip during this sacred month is believed to bring one closer to
            moksha—liberation from the cycle of rebirth.
          </Trans>
        ),
      },
      cultural: {
        title: t('home:significance.cultural.title'),
        icon: significanceIcons.cultural,
        content: (
          <Trans i18nKey="home:significance.cultural.content">
            For centuries, devout pilgrims have practiced Kalpwas during Magh Mela—living
            as ascetics in simple tents on the riverbank for the entire month. They rise
            before dawn for holy dips, follow a strict vegetarian diet, sleep on the
            ground, and spend their days in prayer and meditation. This ancient tradition
            represents the ultimate surrender to spiritual practice and communal devotion.
          </Trans>
        ),
      },
      philosophical: {
        title: t('home:significance.philosophical.title'),
        icon: significanceIcons.philosophical,
        content: (
          <Trans i18nKey="home:significance.philosophical.content">
            Magh Mela&apos;s bathing dates are determined by precise astronomical
            alignments. Makar Sankranti marks the sun&apos;s northward journey, Mauni
            Amavasya occurs on the new moon when celestial energies are believed to be
            most potent, and Maha Shivratri aligns with the darkest night honoring Lord
            Shiva. These cosmic moments are considered gateways to heightened spiritual
            awareness and divine connection.
          </Trans>
        ),
      },
    }),
    [t],
  );

  return (
    <section
      id="magh-mela"
      className="relative py-20 md:py-28 bg-gradient-to-b from-amber-50 via-orange-50 to-amber-100 overflow-hidden"
    >
      {/* Background Image */}
      <img
        className="absolute left-0 top-0 w-full h-full opacity-10 object-cover z-0"
        src={images['significance-hero']}
        alt="The sacred Triveni Sangam"
      />

      {/* Content Container */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 sm:px-12">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block px-4 py-1.5 bg-amber-200/80 text-amber-800 rounded-full text-sm font-medium mb-4">
            {t('common:the-sacred-tradition')}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-display text-amber-900 mb-6">
            {t('home:significance.pot.title')}
          </h2>
          <p className="text-lg md:text-xl text-amber-800/80 max-w-2xl mx-auto leading-relaxed">
            <Trans
              i18nKey="home:significance.pot.lore"
              components={{
                em: <em className="font-semibold" />,
              }}
            />
          </p>
        </div>

        {/* Significance Cards */}
        <ul className="flex flex-col gap-4">
          {Object.keys(significanceOfMagh).map((significance, key) => (
            <SignificanceCard
              {...significanceOfMagh[significance as Significance]}
              key={key}
            />
          ))}
        </ul>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 w-full left-0 h-24 bg-gradient-to-b from-transparent to-green-100" />
    </section>
  );
};
